'use client';

import React, { useEffect, useMemo } from 'react';
import routes from '@/app/routes';
import { IconSticker, ProgressBar, Card } from '@yaradigitallabs/ahua-react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { getMaterialMatchSummary } from '../store/material-match-summary.thunk';
import {
  selectMaterialMatchSummaryData,
  selectMaterialMatchSummaryLoading,
} from '../store/material-match-summary.selectors';

const MatchMakerHomePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const materialMatchSummaryData = useAppSelector(
    selectMaterialMatchSummaryData
  );
  const materialMatchSummaryLoader = useAppSelector(
    selectMaterialMatchSummaryLoading
  );

  useEffect(() => {
    dispatch(getMaterialMatchSummary());
  }, [dispatch]);

  const handleNavigateOnOpen = (path: string) => {
    navigate(path);
  };

  return (
    <div className="px-6 py-6 md:px-14">
      <div className="header mb-4">
        <h1 className="text-2xl font-bold">Welcome to the Matchmaker</h1>
        <p className="text-sm text-gray-400">
          The system to connect SAP items to the plant instrumentation and field
          measurements.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="flex! flex-col! justify-between!">
          <Card.Body orientation="horizontal">
            <div className="flex items-center justify-center">
              <IconSticker
                colorConcept="brand"
                icon="FertilizerSolid"
                size="n"
              />
            </div>
            <div className="flex flex-col flex-1 !p-4 !pt-6">
              <Card.Content
                title="Match materials to report production and bagging"
                css={{ padding: '0' }}
              />
              <div className="flex flex-col gap-2 flex-1">
                <p className="text-sm text-gray-400 mb-2">
                  Link SAP items with soft tags for utilities, byproduct, output
                  & input material for bulk production and bagging activities.
                </p>
                <ProgressBarWithLabel
                  completedSteps={materialMatchSummaryData?.matched_full || 0}
                  numberOfSteps={materialMatchSummaryData?.total_materials || 0}
                  isLoading={materialMatchSummaryLoader}
                />
              </div>
            </div>
          </Card.Body>
          <section aria-label="section-divider">
            <Card.Divider />
            <Card.Tail className="flex flex-row justify-end!">
              <Card.Action
                label="Open"
                variant="ghost"
                disabled={materialMatchSummaryLoader}
                className={`flex-none!`}
                size="s"
                onClick={() =>
                  handleNavigateOnOpen(routes.MATCH_MAKER_OUTPUT_MATERIALS)
                }
              />
            </Card.Tail>
          </section>
        </Card>
        <Card className="flex! flex-col! justify-between!">
          <Card.Body orientation="horizontal" className="flex-1">
            <div className="flex items-center justify-center">
              <IconSticker
                colorConcept="brand"
                icon="FarmIndustrial"
                size="n"
              />
            </div>
            <div className="flex flex-col flex-1 !p-4 !pt-6">
              <Card.Content
                title="Match materials to reconcile stocks"
                css={{ padding: '0' }}
              />
              <div className="flex flex-col justify-between gap-2 flex-1">
                <p className="text-sm text-gray-400 mb-2">
                  Link SAP items with soft tags for raw material & product in
                  liquid bulk, and bagged form.
                </p>
                <ProgressBarWithLabel
                  completedSteps={materialMatchSummaryData?.matched_full || 0}
                  numberOfSteps={materialMatchSummaryData?.total_materials || 0}
                  isLoading={materialMatchSummaryLoader}
                />
              </div>
            </div>
          </Card.Body>
          <section aria-label="section-divider">
            <Card.Divider />
            <Card.Tail className="flex flex-row justify-end!">
              <Card.Action
                label="Open"
                variant="ghost"
                size="s"
                className={`flex-none!`}
                disabled
              />
            </Card.Tail>
          </section>
        </Card>
      </div>
    </div>
  );
};

const ProgressBarWithLabel: React.FC<{
  completedSteps: number;
  numberOfSteps: number;
  isLoading: boolean;
}> = ({ completedSteps, numberOfSteps, isLoading }) => {
  const startLabel = useMemo(() => {
    if (isLoading) {
      return 'Loading...';
    } else if (completedSteps === 0) {
      return 'No SAP material items are matched yet';
    } else {
      return '';
    }
  }, [isLoading, completedSteps]);
  return (
    <div className="flex flex-col items-center gap-2">
      <ProgressBar
        startLabel={startLabel}
        completedSteps={completedSteps}
        css={{ width: '100%' }}
        numberOfSteps={numberOfSteps}
        labels={Array.from({ length: numberOfSteps }).map((_, i) => {
          return `${i + 1} out of ${numberOfSteps} SAP material items are already matched`;
        })}
      />
    </div>
  );
};

export default MatchMakerHomePage;
